import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, Typography, TablePagination, TextField, useTheme, useMediaQuery, CardContent, Stack, InputAdornment, Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import sucVentaService from '../../services/sucVentaService';

const SucVenta: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sucVentas, setSucVentas] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchSucVentas = async (pageNum: number = page, pageSize: number = rowsPerPage, search: string = searchTerm) => {
    try {
      setLoading(true);
      const result = await sucVentaService.getSucVentas({
        pageNumber: pageNum + 1,
        pageSize: pageSize,
        searchTerm: search
      });
      setSucVentas(result.items || []);
      setTotalCount(result.totalCount || 0);
    } catch (error: any) {
      console.error('Error al cargar sucursales de venta:', error);
      const data = error?.response?.data;
      const msg = typeof data === 'string' ? data : data?.error ?? data?.message ?? data?.Message ?? 'Error al cargar sucursales de venta';
      enqueueSnackbar(msg, { variant: 'error' });
      setSucVentas([]);
      setTotalCount(0);
      if (msg?.includes('PageSize') && pageSize > 20) setRowsPerPage(20);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSucVentas();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchSucVentas(0, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    fetchSucVentas(newPage, rowsPerPage, searchTerm);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = Math.min(20, Math.max(5, parseInt(event.target.value, 10) || 10));
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchSucVentas(0, newRowsPerPage, searchTerm);
  };

  const handleDelete = async (nroSuc: number, nroTipoFormulario: string) => {
    if (window.confirm('¿Está seguro de eliminar esta asignación?')) {
      try {
        await sucVentaService.deleteSucVenta(nroSuc, nroTipoFormulario);
        enqueueSnackbar('Asignación eliminada exitosamente', { variant: 'success' });
        fetchSucVentas();
      } catch (error: any) {
        console.error('Error al eliminar asignación:', error);
        enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar asignación', { variant: 'error' });
      }
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Sucursales por Tipo de Formulario
            </Typography>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => navigate('/configuraciones/sucventa/nuevo')}>
              Nueva Asignación
            </Button>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' } }}
            />
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {sucVentas.map((sv) => (
                <Card key={`${sv.nroSuc}-${sv.nroTipoFormulario}`} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Suc: {sv.nroSuc} - {sv.nombreSucursal || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Tipo: {sv.nroTipoFormulario} - {sv.tipoFormulario?.descripcion || 'N/A'}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <IconButton size="small" onClick={() => navigate(`/configuraciones/sucventa/${sv.nroSuc}/${sv.nroTipoFormulario}`)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(sv.nroSuc, sv.nroTipoFormulario)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>N° Sucursal</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Sucursal</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>N° Tipo Formulario</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Tipo Formulario</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sucVentas.map((sv) => (
                    <TableRow key={`${sv.nroSuc}-${sv.nroTipoFormulario}`} hover>
                      <TableCell>{sv.nroSuc}</TableCell>
                      <TableCell>{sv.nombreSucursal || 'N/A'}</TableCell>
                      <TableCell>{sv.nroTipoFormulario}</TableCell>
                      <TableCell>{sv.tipoFormulario?.descripcion || 'N/A'}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => navigate(`/configuraciones/sucventa/${sv.nroSuc}/${sv.nroTipoFormulario}`)} color="primary">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => handleDelete(sv.nroSuc, sv.nroTipoFormulario)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 20]}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default SucVenta;
